export const translations = {
  en: {
    languageLabel: 'English',
    meta: {
      title: 'MAI - Moroccan Artificial Intelligence',
      description:
        'MAI - Moroccan Artificial Intelligence. Local-first AI systems built for regulated, real-world environments.',
    },
    brand: {
      subtitle: 'Moroccan Artificial Intelligence',
      logoAlt: 'MAI Logo',
    },
    nav: {
      links: {
        why: 'Why MAI',
        solutions: 'Solutions',
        platform: 'Platform',
        team: 'Team',
      },
      cta: 'Talk to Us',
      toggleLabel: 'Toggle menu',
      languageLabel: 'Language',
    },
    hero: {
      eyebrow: 'Moroccan-First AI Infrastructure',
      titleLine: 'Enterprise AI for',
      rotatingWords: [
        'Energy',
        'Finance',
        'Agriculture',
        'Mobility',
        'Public Services',
        'Industry',
      ],
      description:
        'We design and deploy AI systems for Moroccan organizations, from data foundations to production deployment. Built for local context, security, and real-world operations.',
      ctaPrimary: 'Start a Pilot',
      ctaSecondary: 'Explore Platform',
      focusLabel: 'Focus areas',
      focusAreas: [
        'Data Platforms',
        'Language AI',
        'Computer Vision',
        'Decision AI',
      ],
      badgeLeftValue: 'Sovereign',
      badgeLeftLabel: 'Data Residency',
      badgeRightValue: 'Enterprise',
      badgeRightLabel: 'MLOps + Monitoring',
      scrollText: 'Scroll to explore',
    },
    stats: {
      eyebrow: 'Why MAI',
      title: 'Local-first AI, built to scale',
      subtitle:
        'We combine Moroccan market knowledge with enterprise engineering to deliver AI systems that are secure, reliable, and ready for production.',
      pillars: [
        {
          tag: 'Governance',
          title: 'Sovereign Data and Compliance',
          description:
            'Deploy on-prem or in sovereign cloud environments with controls designed for Moroccan regulations.',
          points: ['Data residency controls', 'Audit-ready logging', 'Role-based access'],
        },
        {
          tag: 'Language',
          title: 'Arabic and French Language AI',
          description:
            'Language understanding tuned for local context, with a roadmap for dialect-aware expansion.',
          points: ['Arabic and French NLU', 'Dialect-aware roadmap', 'Terminology control'],
        },
        {
          tag: 'Engineering',
          title: 'Production MLOps',
          description:
            'Reliable pipelines from data ingestion to monitoring, built to ship and maintain models in production.',
          points: ['CI/CD for models', 'Drift and quality monitoring', 'Safe rollback'],
        },
        {
          tag: 'Integration',
          title: 'Real-World Deployment',
          description:
            'API-first delivery that integrates with existing platforms, edge systems, and operational workflows.',
          points: ['API-first delivery', 'Edge and IoT ready', 'Works with legacy systems'],
        },
      ],
    },
    solutions: {
      eyebrow: 'What We Build',
      title: 'Solutions for Moroccan Enterprises',
      subtitle:
        "From pilots to production, we deliver secure, scalable AI systems designed for Morocco's operational realities and regulatory landscape.",
      items: {
        platform: {
          title: 'Data and ML Platform',
          description: 'Unified pipelines for ingestion, feature engineering, training, and monitoring.',
          tags: ['MLOps', 'Pipelines'],
        },
        language: {
          title: 'Language AI',
          description: 'Arabic and French NLP tailored to Moroccan context, with dialect-aware expansion.',
          tags: ['Arabic', 'French'],
        },
        vision: {
          title: 'Computer Vision',
          description: 'Vision systems for quality inspection, infrastructure monitoring, and safety.',
          tags: ['Vision', 'Inspection'],
        },
        automation: {
          title: 'Automation and Agents',
          description: 'Workflow automation that connects data, models, and people.',
          tags: ['RPA', 'Workflows'],
        },
        deployment: {
          title: 'Deployment Options',
          description: 'On-prem, private cloud, or hybrid delivery with isolation and observability.',
          tags: ['On-Prem', 'Hybrid'],
        },
        responsible: {
          title: 'Responsible AI',
          description: 'Security, privacy, and governance designed for regulated environments.',
          tags: ['Security', 'Compliance'],
        },
      },
    },
    demo: {
      eyebrow: 'Platform Preview',
      title: 'See MAI in Action',
      subtitle: 'Example workflows showing how the platform handles real-world queries.',
      metaPills: ['On-prem or private cloud', 'Secure by design', 'API-first delivery'],
      terminalTitle: 'mai-engine - sandbox',
      statusConnected: 'Connected',
      loaderText: 'Processing with MAI Engine...',
      demos: [
        {
          prompt: 'Analyze energy demand patterns for Casablanca district',
          response: `Processing time series from smart grid sensors...

Summary:
- Peak demand window: 14:00-18:00 in industrial zones
- Variability drivers: temperature and shift schedules
- Forecast: seasonal uplift expected in Q3

Recommended actions:
- Launch a demand-response pilot in Zone 7B
- Rebalance loads after 21:00`,
        },
        {
          prompt: 'Generate a risk snapshot for agriculture in Souss-Massa',
          response: `Aggregating satellite imagery and weather models...

Risk snapshot:
- Drought risk elevated over the next 90 days
- Yield outlook below recent seasonal baseline
- Soil moisture declining in zones A3 and A7

Recommended actions:
- Prioritize precision irrigation for A3/A7
- Adjust alert thresholds and field inspections`,
        },
        {
          prompt: 'Detect fraud patterns in transaction data for Q4 2025',
          response: `Scanning transactions across multiple institutions...

Findings:
- Clustered micro-transactions across new accounts
- Cross-border patterns flagged for review
- Network links suggest coordinated activity

Actions:
- Escalate to AML workflow
- Activate real-time monitoring on flagged entities`,
        },
      ],
    },
    team: {
      eyebrow: 'Our People',
      title: 'Founding Team',
      subtitle:
        'A founding team combining product, engineering, and business to build local-first AI systems for Morocco.',
      members: {
        simo: {
          name: 'Simo Oubia',
          role: 'Co-Founder',
          bio: 'Product and strategy lead focused on sovereign AI infrastructure and enterprise delivery in Morocco.',
        },
        anass: {
          name: 'Anass Nassiri',
          role: 'Co-Founder',
          bio: 'Operations and growth lead focused on partnerships, go-to-market, and execution.',
        },
        ismail: {
          name: 'Ismail Ahourai',
          role: 'Co-Founder',
          bio: "Engineering lead building MAI's platform, data pipelines, and deployment tooling.",
        },
      },
      linkLabel: 'Profile',
      profileAria: 'Profile',
    },
    contact: {
      eyebrow: 'Start a Conversation',
      title: 'Contact',
      subtitle:
        'Tell us about your use case, data sources, and constraints. We will shape a practical pilot and deployment path together.',
      headquartersLabel: 'Headquarters',
      headquartersValue: 'Casablanca, Morocco',
      emailLabel: 'Email',
      mapLabel: 'Casablanca, Morocco',
      nextTitle: 'What happens next',
      nextItems: [
        'Discovery call to scope your use case',
        'Data and feasibility review',
        'Pilot plan and delivery timeline',
      ],
      form: {
        nameLabel: 'Full Name',
        emailLabel: 'Email',
        companyLabel: 'Company',
        messageLabel: 'Message',
        namePlaceholder: 'Your full name',
        emailPlaceholder: 'name@company.com',
        companyPlaceholder: 'Organization',
        messagePlaceholder: 'Use case, data sources, timeline...',
        submitLabel: 'Send Request',
      },
    },
    footer: {
      brandDesc:
        'Moroccan-first AI infrastructure and applied research for regulated, real-world environments.',
      quickLinksTitle: 'Quick Links',
      quickLinks: {
        home: 'Home',
        why: 'Why MAI',
        solutions: 'Solutions',
        platform: 'Platform',
        team: 'Team',
        contact: 'Contact',
      },
      solutionsTitle: 'Solutions',
      solutionsLinks: {
        platform: 'Data and ML Platform',
        language: 'Language AI',
        vision: 'Computer Vision',
        automation: 'Automation and Agents',
        responsible: 'Responsible AI',
      },
      contactTitle: 'Contact',
      contactLocation: 'Casablanca, Morocco',
      contactEmail: 'contact@mai.ma',
      copyright:
        '© {year} MAI - Moroccan Artificial Intelligence. All rights reserved.',
      bottomNote: 'Casablanca, Morocco',
    },
    scrollTop: {
      ariaLabel: 'Scroll to top',
    },
  },
  fr: {
    languageLabel: 'Français',
    meta: {
      title: 'MAI - Intelligence Artificielle Marocaine',
      description:
        'MAI - Intelligence Artificielle Marocaine. Des systèmes IA locaux pour des environnements réels et réglementés.',
    },
    brand: {
      subtitle: 'Intelligence Artificielle Marocaine',
      logoAlt: 'Logo MAI',
    },
    nav: {
      links: {
        why: 'Pourquoi MAI',
        solutions: 'Solutions',
        platform: 'Plateforme',
        team: 'Équipe',
      },
      cta: 'Parlons-en',
      toggleLabel: 'Basculer le menu',
      languageLabel: 'Langue',
    },
    hero: {
      eyebrow: 'Infrastructure IA pensée pour le Maroc',
      titleLine: "IA d'entreprise pour",
      rotatingWords: [
        'Énergie',
        'Finance',
        'Agriculture',
        'Mobilité',
        'Services publics',
        'Industrie',
      ],
      description:
        "Nous concevons et déployons des systèmes IA pour les organisations marocaines, des fondations data jusqu'à la mise en production. Pensés pour le contexte local, la sécurité et l'opérationnel.",
      ctaPrimary: 'Démarrer un pilote',
      ctaSecondary: 'Explorer la plateforme',
      focusLabel: 'Domaines clés',
      focusAreas: [
        'Plateformes data',
        'IA du langage',
        'Vision par ordinateur',
        'IA décisionnelle',
      ],
      badgeLeftValue: 'Souverain',
      badgeLeftLabel: 'Résidence des données',
      badgeRightValue: 'Entreprise',
      badgeRightLabel: 'MLOps + monitoring',
      scrollText: 'Faites défiler pour découvrir',
    },
    stats: {
      eyebrow: 'Pourquoi MAI',
      title: "IA locale, prête à l'échelle",
      subtitle:
        "Nous combinons la connaissance du marché marocain et l'ingénierie entreprise pour livrer des systèmes IA sécurisés, fiables et prêts pour la production.",
      pillars: [
        {
          tag: 'Gouvernance',
          title: 'Données souveraines et conformité',
          description:
            'Déploiement sur site ou en cloud souverain avec des contrôles adaptés aux réglementations marocaines.',
          points: ['Contrôles de résidence des données', 'Journalisation prête à audit', "Accès basé sur les rôles"],
        },
        {
          tag: 'Langue',
          title: 'IA du langage arabe et français',
          description:
            'Compréhension linguistique adaptée au contexte local avec une feuille de route pour les dialectes.',
          points: ['NLU arabe et français', 'Feuille de route dialectes', 'Contrôle terminologique'],
        },
        {
          tag: 'Ingénierie',
          title: 'MLOps en production',
          description:
            'Pipelines fiables de l’ingestion des données à la supervision, conçus pour la production.',
          points: ['CI/CD pour modèles', 'Surveillance du drift et qualité', 'Retour arrière sécurisé'],
        },
        {
          tag: 'Intégration',
          title: 'Déploiement terrain',
          description:
            "Livraison API-first qui s'intègre aux plateformes existantes, aux systèmes edge et aux workflows opérationnels.",
          points: ['Livraison API-first', 'Prêt pour edge et IoT', 'Compatible systèmes legacy'],
        },
      ],
    },
    solutions: {
      eyebrow: 'Ce que nous construisons',
      title: 'Solutions pour les entreprises marocaines',
      subtitle:
        "Du pilote à la production, nous livrons des systèmes IA sécurisés et évolutifs adaptés aux réalités opérationnelles et réglementaires du Maroc.",
      items: {
        platform: {
          title: 'Plateforme data & ML',
          description: 'Pipelines unifiés pour ingestion, feature engineering, entraînement et monitoring.',
          tags: ['MLOps', 'Pipelines'],
        },
        language: {
          title: 'IA du langage',
          description: 'NLP arabe et français adapté au contexte marocain, avec extension dialectale.',
          tags: ['Arabe', 'Français'],
        },
        vision: {
          title: 'Vision par ordinateur',
          description: 'Systèmes de vision pour contrôle qualité, suivi d’infrastructure et sécurité.',
          tags: ['Vision', 'Inspection'],
        },
        automation: {
          title: 'Automatisation et agents',
          description: 'Automatisation des workflows reliant données, modèles et équipes.',
          tags: ['RPA', 'Workflows'],
        },
        deployment: {
          title: 'Options de déploiement',
          description: 'Sur site, cloud privé ou hybride avec isolation et observabilité.',
          tags: ['Sur site', 'Hybride'],
        },
        responsible: {
          title: 'IA responsable',
          description: 'Sécurité, confidentialité et gouvernance pour environnements réglementés.',
          tags: ['Sécurité', 'Conformité'],
        },
      },
    },
    demo: {
      eyebrow: 'Aperçu de la plateforme',
      title: 'Voir MAI en action',
      subtitle: 'Exemples de workflows montrant comment la plateforme traite des requêtes réelles.',
      metaPills: ['Sur site ou cloud privé', 'Sécurisé par conception', 'API-first'],
      terminalTitle: 'mai-engine - sandbox',
      statusConnected: 'Connecté',
      loaderText: 'Traitement avec MAI Engine...',
      demos: [
        {
          prompt: "Analyser les profils de demande énergétique d'un district de Casablanca",
          response: `Traitement des séries temporelles des capteurs du réseau...

Résumé :
- Fenêtre de pic : 14:00-18:00 en zones industrielles
- Variabilité : température et horaires d'équipe
- Prévision : hausse saisonnière en T3

Actions recommandées :
- Lancer un pilote demand-response en Zone 7B
- Rééquilibrer les charges après 21:00`,
        },
        {
          prompt: 'Générer un aperçu des risques agricoles pour Souss-Massa',
          response: `Agrégation d’images satellites et modèles météo...

Aperçu des risques :
- Risque de sécheresse élevé sur 90 jours
- Rendement attendu sous la moyenne saisonnière
- Humidité des sols en baisse zones A3 et A7

Actions recommandées :
- Prioriser l’irrigation de précision A3/A7
- Ajuster les seuils d’alerte et inspections`,
        },
        {
          prompt: 'Détecter des schémas de fraude sur les transactions T4 2025',
          response: `Analyse des transactions multi-institutions...

Constats :
- Micro-transactions groupées sur nouveaux comptes
- Schémas transfrontaliers signalés
- Liens réseau suggérant coordination

Actions :
- Escalader vers workflow AML
- Activer le monitoring temps réel`,
        },
      ],
    },
    team: {
      eyebrow: 'Notre équipe',
      title: 'Équipe fondatrice',
      subtitle:
        'Une équipe fondatrice qui combine produit, ingénierie et business pour bâtir une IA locale pour le Maroc.',
      members: {
        simo: {
          name: 'Simo Oubia',
          role: 'Co-fondateur',
          bio: "Responsable produit et stratégie, orienté infrastructure IA souveraine et delivery entreprise au Maroc.",
        },
        anass: {
          name: 'Anass Nassiri',
          role: 'Co-fondateur',
          bio: 'Responsable opérations et croissance, partenariats, go-to-market et exécution.',
        },
        ismail: {
          name: 'Ismail Ahourai',
          role: 'Co-fondateur',
          bio: "Responsable ingénierie, construit la plateforme, les pipelines data et les outils de déploiement.",
        },
      },
      linkLabel: 'Profil',
      profileAria: 'Profil',
    },
    contact: {
      eyebrow: 'Démarrer une discussion',
      title: 'Contact',
      subtitle:
        "Parlez-nous de votre cas d'usage, des sources de données et des contraintes. Nous construirons un pilote pragmatique et un plan de déploiement.",
      headquartersLabel: 'Siège',
      headquartersValue: 'Casablanca, Maroc',
      emailLabel: 'Email',
      mapLabel: 'Casablanca, Maroc',
      nextTitle: 'Et ensuite',
      nextItems: [
        'Appel de découverte pour cadrer le cas d’usage',
        'Analyse des données et faisabilité',
        'Plan pilote et calendrier de livraison',
      ],
      form: {
        nameLabel: 'Nom complet',
        emailLabel: 'Email',
        companyLabel: 'Entreprise',
        messageLabel: 'Message',
        namePlaceholder: 'Votre nom complet',
        emailPlaceholder: 'nom@entreprise.com',
        companyPlaceholder: 'Organisation',
        messagePlaceholder: "Cas d'usage, sources de données, calendrier...",
        submitLabel: 'Envoyer la demande',
      },
    },
    footer: {
      brandDesc:
        "Infrastructure IA locale et recherche appliquée pour des environnements réels et réglementés.",
      quickLinksTitle: 'Liens rapides',
      quickLinks: {
        home: 'Accueil',
        why: 'Pourquoi MAI',
        solutions: 'Solutions',
        platform: 'Plateforme',
        team: 'Équipe',
        contact: 'Contact',
      },
      solutionsTitle: 'Solutions',
      solutionsLinks: {
        platform: 'Plateforme data & ML',
        language: 'IA du langage',
        vision: 'Vision par ordinateur',
        automation: 'Automatisation et agents',
        responsible: 'IA responsable',
      },
      contactTitle: 'Contact',
      contactLocation: 'Casablanca, Maroc',
      contactEmail: 'contact@mai.ma',
      copyright:
        "© {year} MAI - Intelligence Artificielle Marocaine. Tous droits réservés.",
      bottomNote: 'Casablanca, Maroc',
    },
    scrollTop: {
      ariaLabel: 'Revenir en haut',
    },
  },
  ary: {
    languageLabel: 'الدارجة',
    meta: {
      title: 'MAI - الذكاء الاصطناعي المغربي',
      description:
        'MAI - الذكاء الاصطناعي المغربي. أنظمة ذكاء اصطناعي محلية لبيئات حقيقية ومقننة.',
    },
    brand: {
      subtitle: 'الذكاء الاصطناعي المغربي',
      logoAlt: 'شعار MAI',
    },
    nav: {
      links: {
        why: 'علاش MAI',
        solutions: 'الحلول',
        platform: 'المنصة',
        team: 'الفريق',
      },
      cta: 'هدّر معنا',
      toggleLabel: 'فتح/إغلاق القائمة',
      languageLabel: 'اللغة',
    },
    hero: {
      eyebrow: 'بنية تحتية ديال الذكاء الاصطناعي ديال المغرب',
      titleLine: 'ذكاء اصطناعي ديال الشركات فـ',
      rotatingWords: [
        'الطاقة',
        'المالية',
        'الفلاحة',
        'التنقّل',
        'الخدمات العمومية',
        'الصناعة',
      ],
      description:
        'كنصمّمو ونطبّقو أنظمة الذكاء الاصطناعي للمؤسسات المغربية، من تأسيس البيانات حتى للإنتاج. مبنية على السياق المحلي، الأمان، والخدمة الواقعية.',
      ctaPrimary: 'بداّو التجربة',
      ctaSecondary: 'كتشف المنصة',
      focusLabel: 'مجالات التركيز',
      focusAreas: [
        'منصات البيانات',
        'ذكاء اللغة',
        'الرؤية الحاسوبية',
        'ذكاء القرار',
      ],
      badgeLeftValue: 'سيادي',
      badgeLeftLabel: 'إقامة البيانات',
      badgeRightValue: 'مقاولات',
      badgeRightLabel: 'MLOps والمراقبة',
      scrollText: 'هبط باش تكتاشف',
    },
    stats: {
      eyebrow: 'علاش MAI',
      title: 'ذكاء محلي وقابل للتوسيع',
      subtitle:
        'كنجمعو بين معرفة السوق المغربي والهندسة المؤسساتية باش نقدمو أنظمة آمنة، موثوقة، ومجهزة للإنتاج.',
      pillars: [
        {
          tag: 'الحكامة',
          title: 'بيانات سيادية والتوافق',
          description:
            'نشر داخل المقر أو كلاود سيادي مع تحكمات مطابقة للقوانين المغربية.',
          points: ['تحكم فإقامة البيانات', 'سجلات جاهزة للتدقيق', 'صلاحيات حسب الأدوار'],
        },
        {
          tag: 'اللغة',
          title: 'ذكاء لغوي بالعربية والفرنسية',
          description:
            'فهم لغوي مضبوط على السياق المحلي مع خارطة طريق للهجات.',
          points: ['فهم العربية والفرنسية', 'خارطة طريق للهجات', 'التحكم فالمصطلحات'],
        },
        {
          tag: 'الهندسة',
          title: 'MLOps فالإنتاج',
          description:
            'قنوات موثوقة من جمع البيانات حتى المراقبة، مصممة للإنتاج.',
          points: ['CI/CD للنماذج', 'مراقبة الانجراف والجودة', 'رجوع آمن'],
        },
        {
          tag: 'الإدماج',
          title: 'نشر فالعالم الحقيقي',
          description:
            'تسليم API-first كيتكامل مع الأنظمة الحالية، والأنظمة الطرفية، والعمليات اليومية.',
          points: ['تسليم API-first', 'جاهز للحافة و IoT', 'كيخدم مع الأنظمة القديمة'],
        },
      ],
    },
    solutions: {
      eyebrow: 'شنو كنطوّرو',
      title: 'حلول للمقاولات المغربية',
      subtitle:
        'من التجارب حتى للإنتاج، كنقدمو أنظمة آمنة وقابلة للتوسع ومناسبة لواقع المغرب.',
      items: {
        platform: {
          title: 'منصة البيانات و ML',
          description: 'قنوات موحدة للجمع، الهندسة، التدريب، والمراقبة.',
          tags: ['MLOps', 'Pipelines'],
        },
        language: {
          title: 'ذكاء اللغة',
          description: 'NLP بالعربية والفرنسية مع احترام السياق المغربي والتوسع للهجات.',
          tags: ['العربية', 'الفرنسية'],
        },
        vision: {
          title: 'الرؤية الحاسوبية',
          description: 'أنظمة رؤية لمراقبة الجودة، البنية التحتية، والسلامة.',
          tags: ['الرؤية', 'التفتيش'],
        },
        automation: {
          title: 'الأتمتة والـAgents',
          description: 'أتمتة للعمليات كتربط البيانات بالنماذج وبالناس.',
          tags: ['RPA', 'Workflows'],
        },
        deployment: {
          title: 'خيارات النشر',
          description: 'داخل المقر، كلاود خاص، أو هجين مع العزل والمراقبة.',
          tags: ['On-Prem', 'Hybrid'],
        },
        responsible: {
          title: 'ذكاء اصطناعي مسؤول',
          description: 'الأمن، الخصوصية، والحكامة للبيئات المقننة.',
          tags: ['الأمن', 'التوافق'],
        },
      },
    },
    demo: {
      eyebrow: 'نظرة على المنصة',
      title: 'شوف MAI خدام',
      subtitle: 'أمثلة ديال workflows كيبينو كيفاش المنصة كتعالج طلبات حقيقية.',
      metaPills: ['داخل المقر ولا كلاود خاص', 'مؤمّن من البداية', 'API-first'],
      terminalTitle: 'mai-engine - تجريبي',
      statusConnected: 'متصل',
      loaderText: 'كنعالج بالـ MAI Engine...',
      demos: [
        {
          prompt: 'حلل أنماط طلب الطاقة فحي من الدار البيضاء',
          response: `كنعالج سلاسل زمنية من حساسات الشبكة...

الملخص:
- وقت الذروة: 14:00-18:00 فالمناطق الصناعية
- العوامل: الحرارة وجداول الشغل
- التوقع: ارتفاع موسمي فـ Q3

الخطوات المقترحة:
- إطلاق تجربة demand-response فـ Zone 7B
- إعادة التوازن بعد 21:00`,
        },
        {
          prompt: 'عطينا لمحة على مخاطر الفلاحة فـ سوس ماسة',
          response: `كنجمع صور الأقمار الصناعية ونماذج الطقس...

لمحة المخاطر:
- خطر الجفاف طالع فـ 90 يوم الجايين
- توقع المردودية أقل من المتوسط
- رطوبة التربة هابطة فـ A3 و A7

الخطوات المقترحة:
- نعطيو الأولوية للسقي الدقيق فـ A3/A7
- نبدلو عتبات الإنذار و الزيارات الميدانية`,
        },
        {
          prompt: 'كشف أنماط الاحتيال فمعطيات المعاملات ديال Q4 2025',
          response: `كنفحص المعاملات عبر مؤسسات متعددة...

الملاحظات:
- معاملات صغيرة مجمعة فحسابات جديدة
- أنماط عبر الحدود مبينة للمراجعة
- روابط شبكية كتشير لتنسيق

الإجراءات:
- تصعيد لworkflow ديال AML
- تفعيل المراقبة فـ الوقت الحقيقي`,
        },
      ],
    },
    team: {
      eyebrow: 'ناسنا',
      title: 'الفريق المؤسس',
      subtitle:
        'فريق مؤسس كيجمع بين المنتج، الهندسة، والبزنس باش يبني ذكاء محلي للمغرب.',
      members: {
        simo: {
          name: 'Simo Oubia',
          role: 'مؤسس مشارك',
          bio: 'مسؤول على المنتج والاستراتيجية، مركز على البنية السيادية وتسليم الحلول للمقاولات.',
        },
        anass: {
          name: 'Anass Nassiri',
          role: 'مؤسس مشارك',
          bio: 'مسؤول على العمليات والنمو، الشراكات، والتسويق والتنفيذ.',
        },
        ismail: {
          name: 'Ismail Ahourai',
          role: 'مؤسس مشارك',
          bio: 'مسؤول هندسي كيبني المنصة، قنوات البيانات، وأدوات النشر.',
        },
      },
      linkLabel: 'البروفايل',
      profileAria: 'البروفايل',
    },
    contact: {
      eyebrow: 'بداّو التواصل',
      title: 'تواصل',
      subtitle:
        'قول لينا على الاستعمال، مصادر البيانات، والقيود. غادي نبنيو تجربة عملية وخطة نشر واضحة.',
      headquartersLabel: 'المقر',
      headquartersValue: 'الدار البيضاء، المغرب',
      emailLabel: 'الإيميل',
      mapLabel: 'الدار البيضاء، المغرب',
      nextTitle: 'شنو غادي يوقع من بعد',
      nextItems: [
        'مكالمة تعريفية لتحديد الاستعمال',
        'مراجعة البيانات والجدوى',
        'خطة تجربة وجدول التنفيذ',
      ],
      form: {
        nameLabel: 'الاسم الكامل',
        emailLabel: 'الإيميل',
        companyLabel: 'المؤسسة',
        messageLabel: 'الرسالة',
        namePlaceholder: 'الاسم الكامل ديالك',
        emailPlaceholder: 'name@company.com',
        companyPlaceholder: 'المؤسسة',
        messagePlaceholder: 'حالة الاستعمال، مصادر البيانات، المدة...',
        submitLabel: 'صيفط الطلب',
      },
    },
    footer: {
      brandDesc:
        'بنية تحتية ديال الذكاء الاصطناعي وبحث تطبيقي للبيئات الحقيقية والمقننة.',
      quickLinksTitle: 'روابط سريعة',
      quickLinks: {
        home: 'الرئيسية',
        why: 'علاش MAI',
        solutions: 'الحلول',
        platform: 'المنصة',
        team: 'الفريق',
        contact: 'تواصل',
      },
      solutionsTitle: 'الحلول',
      solutionsLinks: {
        platform: 'منصة البيانات و ML',
        language: 'ذكاء اللغة',
        vision: 'الرؤية الحاسوبية',
        automation: 'الأتمتة والـAgents',
        responsible: 'ذكاء اصطناعي مسؤول',
      },
      contactTitle: 'تواصل',
      contactLocation: 'الدار البيضاء، المغرب',
      contactEmail: 'contact@mai.ma',
      copyright:
        '© {year} MAI - الذكاء الاصطناعي المغربي. جميع الحقوق محفوظة.',
      bottomNote: 'الدار البيضاء، المغرب',
    },
    scrollTop: {
      ariaLabel: 'الرجوع للأعلى',
    },
  },
};

export const rtlLanguages = new Set(['ary']);
